import { create } from 'zustand'
import { toast } from 'sonner'
import { authApi } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { captureEvent } from '@/lib/posthog'
import type { TourStep } from './tourSteps'

// UI state for the product tour. Deliberately not persisted: the durable fact
// ("has this user finished, skipped or snoozed it") lives on the server
// (users.tour_state) so it follows the user across devices, and is written by
// the finish / skip / snooze actions below.

export type TourPhase = 'idle' | 'invite' | 'touring'
export type TourSource = 'invite' | 'replay'

interface TourState {
  phase: TourPhase
  steps: TourStep[]
  index: number
  source: TourSource | null
  setSteps: (steps: TourStep[]) => void
  offerInvite: () => void
  start: (source: TourSource) => void
  next: () => Promise<void>
  back: () => void
  finish: () => Promise<void>
  skip: () => Promise<void>
  snooze: () => Promise<void>
}

const REPLAY_HINT = 'You can replay it any time from your profile menu.'

async function persist(action: 'complete' | 'skip' | 'snooze'): Promise<void> {
  try {
    const res = await authApi.updateTourState(action)
    useAuthStore.getState().updateUser(res.data)
  } catch {
    // The tour must close regardless. Worst case the invite is offered again
    // on the next load, which is the safe direction to fail in.
    console.warn('Failed to save product tour state')
  }
}

export const useTourStore = create<TourState>((set, get) => ({
  phase: 'idle',
  steps: [],
  index: 0,
  source: null,

  setSteps: (steps) => {
    set({ steps })
    // If the sidebar shrank under a running tour (permissions refreshed), keep the index valid.
    if (get().index >= steps.length) set({ index: Math.max(0, steps.length - 1) })
  },

  offerInvite: () => {
    const { phase, steps } = get()
    if (phase !== 'idle' || steps.length === 0) return
    set({ phase: 'invite' })
    captureEvent('product_tour_invite_shown')
  },

  start: (source) => {
    const { steps } = get()
    if (steps.length === 0) return
    set({ phase: 'touring', index: 0, source })
    captureEvent('product_tour_started', { source, steps: steps.length })
    captureEvent('product_tour_step_viewed', { step_key: steps[0].key, index: 0 })
  },

  next: async () => {
    const { index, steps } = get()
    if (index >= steps.length - 1) return get().finish()
    set({ index: index + 1 })
    captureEvent('product_tour_step_viewed', { step_key: steps[index + 1].key, index: index + 1 })
  },

  back: () => {
    const { index, steps } = get()
    if (index <= 0) return
    set({ index: index - 1 })
    captureEvent('product_tour_step_viewed', { step_key: steps[index - 1].key, index: index - 1 })
  },

  finish: async () => {
    const { source } = get()
    set({ phase: 'idle', index: 0, source: null })
    captureEvent('product_tour_completed', { source })
    toast(`Tour complete. ${REPLAY_HINT}`)
    await persist('complete')
  },

  skip: async () => {
    const { index, steps, source } = get()
    set({ phase: 'idle', index: 0, source: null })
    captureEvent('product_tour_skipped', { step_key: steps[index]?.key ?? null, index, source })
    toast(`Tour skipped. ${REPLAY_HINT}`)
    await persist('skip')
  },

  snooze: async () => {
    set({ phase: 'idle', index: 0, source: null })
    captureEvent('product_tour_invite_snoozed')
    toast(`No problem. ${REPLAY_HINT}`)
    await persist('snooze')
  },
}))
